import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import type { WallTakeoffData, WallSurface, WallOpening, WallElectrical } from '../data/kimbarkBasementWallTakeoff';

// Raw Z-up → Three.js Y-up (matches MultiSceneViewer sceneQuaternion)
const SPLAT_QUATERNION = new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));

const COLOR_COMPLETE  = 0x22c55e;  // green-500 — matches checkmark, visible on white drywall
const COLOR_REMAINING = 0xa855f7;  // purple-500
const COLOR_OPENING   = 0xf97316;  // orange-500

function quadCentroid(pts: [number, number, number][]): [number, number, number] {
  const n = pts.length;
  return [
    pts.reduce((s, p) => s + p[0], 0) / n,
    pts.reduce((s, p) => s + p[1], 0) / n,
    pts.reduce((s, p) => s + p[2], 0) / n,
  ];
}

function topEdgeMid(pts: [number, number, number][]): [number, number, number] {
  // "top" = largest raw_z (less negative = higher in world Y after Z-up→Y-up transform)
  const sorted = [...pts].sort((a, b) => b[2] - a[2]);
  return [
    (sorted[0][0] + sorted[1][0]) / 2,
    (sorted[0][1] + sorted[1][1]) / 2,
    (sorted[0][2] + sorted[1][2]) / 2,
  ];
}

function rawToWorld(raw: [number, number, number]): THREE.Vector3 {
  return new THREE.Vector3(raw[0], raw[2], -raw[1]);
}

function buildQuadFill(pts: [number, number, number][], color: number, opacity: number): THREE.Mesh {
  const [P0, P1, P2, P3] = pts;
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute([
    ...P0, ...P1, ...P2,
    ...P0, ...P2, ...P3,
  ], 3));
  const mat = new THREE.MeshBasicMaterial({
    color, transparent: true, opacity, side: THREE.DoubleSide, depthTest: false,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.renderOrder = 9;
  return mesh;
}

function buildQuadOutline(pts: [number, number, number][], color: number, opacity = 0.9): THREE.LineSegments {
  const [P0, P1, P2, P3] = pts;
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute([
    ...P0, ...P1,
    ...P1, ...P2,
    ...P2, ...P3,
    ...P3, ...P0,
  ], 3));
  const mat = new THREE.LineBasicMaterial({ color, transparent: true, opacity, depthTest: false });
  const lines = new THREE.LineSegments(geo, mat);
  lines.renderOrder = 10;
  return lines;
}

type LabelKind = 'surface' | 'opening' | 'electrical';

interface LabelSpec {
  key: string;
  rawPoint: [number, number, number];
  kind: LabelKind;
  surfaceId?: string;
  openingId?: string;
  electricalId?: string;
}

interface Props {
  viewerRef: React.MutableRefObject<{
    threeScene?: THREE.Scene;
    camera?: THREE.PerspectiveCamera;
    renderer?: THREE.WebGLRenderer;
  }>;
  data: WallTakeoffData;
  highlightedId: string | null;
}

export function WallTakeoffOverlay({ viewerRef, data, highlightedId }: Props) {
  const rafRef       = useRef<number | null>(null);
  const elRefsRef    = useRef<(HTMLDivElement | null)[]>([]);
  const fillMatsRef  = useRef<Map<string, THREE.MeshBasicMaterial>>(new Map());
  const outlineMatsRef = useRef<Map<string, THREE.LineBasicMaterial>>(new Map());
  const openingFillMatsRef = useRef<Map<string, THREE.MeshBasicMaterial>>(new Map());
  const electricalFillMatsRef = useRef<Map<string, THREE.MeshBasicMaterial>>(new Map());

  // One card per surface (at top edge), one card per opening (at centroid), one icon per electrical
  const labelSpecs: LabelSpec[] = useMemo(() => {
    const specs: LabelSpec[] = [];
    for (const s of data.surfaces) {
      specs.push({ key: `surface_${s.id}`, rawPoint: topEdgeMid(s.points), kind: 'surface', surfaceId: s.id });
    }
    for (const o of data.openings) {
      specs.push({ key: `opening_${o.id}`, rawPoint: quadCentroid(o.points), kind: 'opening', openingId: o.id });
    }
    for (const e of data.electrical) {
      specs.push({ key: `elec_${e.id}`, rawPoint: quadCentroid(e.points), kind: 'electrical', electricalId: e.id });
    }
    return specs;
  }, [data]);

  const anchorVectors = useMemo(
    () => labelSpecs.map(s => rawToWorld(s.rawPoint)),
    [labelSpecs],
  );

  // --- Build Three.js geometry ---
  useEffect(() => {
    const scene = viewerRef.current.threeScene;
    if (!scene) return;

    fillMatsRef.current.clear();
    outlineMatsRef.current.clear();
    openingFillMatsRef.current.clear();
    electricalFillMatsRef.current.clear();
    const groups: THREE.Group[] = [];

    for (const s of data.surfaces) {
      const color       = s.status === 'complete' ? COLOR_COMPLETE : COLOR_REMAINING;
      const fillOpacity = s.status === 'complete' ? 0.22 : 0.30;

      const fill    = buildQuadFill(s.points, color, fillOpacity);
      const outline = buildQuadOutline(s.points, color, 0.9);

      fillMatsRef.current.set(s.id, fill.material as THREE.MeshBasicMaterial);
      outlineMatsRef.current.set(s.id, outline.material as THREE.LineBasicMaterial);

      const group = new THREE.Group();
      group.quaternion.copy(SPLAT_QUATERNION);
      group.add(fill, outline);
      scene.add(group);
      groups.push(group);
    }

    for (const o of data.openings) {
      const fill    = buildQuadFill(o.points, COLOR_OPENING, 0.25);
      const outline = buildQuadOutline(o.points, COLOR_OPENING, 0.9);
      openingFillMatsRef.current.set(o.id, fill.material as THREE.MeshBasicMaterial);
      const group   = new THREE.Group();
      group.quaternion.copy(SPLAT_QUATERNION);
      group.add(fill, outline);
      scene.add(group);
      groups.push(group);
    }

    for (const e of data.electrical) {
      const fill = buildQuadFill(e.points, 0x22d3ee, 0.10);
      electricalFillMatsRef.current.set(e.id, fill.material as THREE.MeshBasicMaterial);
      const group = new THREE.Group();
      group.quaternion.copy(SPLAT_QUATERNION);
      group.add(fill);
      scene.add(group);
      groups.push(group);
    }

    // RAF: project label anchors to screen space
    const proj = new THREE.Vector3();
    const tick = () => {
      rafRef.current = requestAnimationFrame(tick);
      const camera = viewerRef.current.camera;
      const canvas = viewerRef.current.renderer?.domElement;
      if (!camera || !canvas) return;
      const rect = canvas.getBoundingClientRect();
      anchorVectors.forEach((anchor, i) => {
        const el = elRefsRef.current[i];
        if (!el) return;
        proj.copy(anchor).project(camera);
        if (proj.z > 1) { el.style.display = 'none'; return; }
        const x = (proj.x + 1) / 2 * rect.width  + rect.left;
        const y = (1 - proj.y) / 2 * rect.height + rect.top;
        el.style.display = 'block';
        const vy = el.dataset.voffset ?? '-100%';
        el.style.transform = `translate(-50%,${vy}) translate(${x}px,${y}px)`;
      });
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      for (const g of groups) {
        scene.remove(g);
        g.traverse(child => {
          if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments) {
            child.geometry.dispose();
            (child.material as THREE.Material).dispose();
          }
        });
      }
      fillMatsRef.current.clear();
      outlineMatsRef.current.clear();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, viewerRef]);

  // Boost opacity for highlighted surface / openings / electrical
  useEffect(() => {
    const allOpenings = highlightedId === '__openings__';
    const allElec     = highlightedId === '__electrical__';

    fillMatsRef.current.forEach((mat, id) => {
      const surface = data.surfaces.find(s => s.id === id);
      if (!surface) return;
      const base = surface.status === 'complete' ? 0.22 : 0.30;
      mat.opacity = id === highlightedId ? Math.min(base + 0.25, 0.65) : base;
    });
    outlineMatsRef.current.forEach((mat, id) => {
      mat.opacity = id === highlightedId ? 1.0 : 0.9;
    });
    openingFillMatsRef.current.forEach(mat => {
      mat.opacity = allOpenings ? 0.55 : 0.25;
    });
    electricalFillMatsRef.current.forEach(mat => {
      mat.opacity = allElec ? 0.40 : 0.10;
    });
  }, [highlightedId, data]);

  // --- Render label elements ---
  const cardBase: React.CSSProperties = {
    position: 'fixed', top: 0, left: 0,
    pointerEvents: 'none', zIndex: 1020,
    display: 'none',
    borderRadius: 8,
    padding: '7px 11px',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    lineHeight: 1.4,
    marginBottom: 4,
  };

  function renderLabel(spec: LabelSpec, index: number) {
    if (spec.kind === 'surface') {
      const surface = data.surfaces.find(s => s.id === spec.surfaceId)!;
      const isComplete = surface.status === 'complete';

      if (isComplete) {
        return (
          <div
            key={spec.key}
            ref={el => { elRefsRef.current[index] = el; }}
            style={{
              ...cardBase,
              background: 'rgba(10,12,16,0.88)',
              border: '1px solid rgba(255,255,255,0.18)',
            }}
          >
            {/* Green checkmark row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, marginBottom: 3 }}>
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="7" r="6" stroke="#22c55e" strokeWidth="1.5"/>
                <polyline points="4,7 6.5,9.5 10,5" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#ffffff', letterSpacing: '0.02em' }}>Already complete</span>
            </div>
            <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.01em' }}>
              {surface.widthFt.toFixed(1)} ft × {surface.heightFt.toFixed(1)} ft · <strong style={{ color: 'rgba(255,255,255,0.85)' }}>{surface.areaSqFt} sq ft</strong>
            </div>
          </div>
        );
      } else {
        return (
          <div
            key={spec.key}
            ref={el => { elRefsRef.current[index] = el; }}
            style={{
              ...cardBase,
              background: 'rgba(30,10,50,0.88)',
              border: '1px solid #a855f7',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#c084fc', letterSpacing: '0.02em', marginBottom: 3 }}>
              Remaining
            </div>
            <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.6)' }}>
              {surface.widthFt.toFixed(1)} ft × {surface.heightFt.toFixed(1)} ft · <strong style={{ color: 'rgba(255,255,255,0.85)' }}>{surface.areaSqFt} sq ft</strong>
            </div>
          </div>
        );
      }
    }

    if (spec.kind === 'opening') {
      const opening = data.openings.find(o => o.id === spec.openingId)!;
      return (
        <div
          key={spec.key}
          ref={el => { elRefsRef.current[index] = el; }}
          style={{
            ...cardBase,
            background: 'rgba(10,12,16,0.85)',
            border: '1px solid rgba(249,115,22,0.6)',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: '#ffffff', marginBottom: 2 }}>
            ~{opening.widthIn}″ × {opening.heightIn}″
          </div>
          <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.55)' }}>
            {opening.areaSqFt} sq ft
          </div>
        </div>
      );
    }

    if (spec.kind === 'electrical') {
      const elec = data.electrical.find(e => e.id === spec.electricalId)!;
      const isOutlet = elec.type === 'outlet';
      return (
        <div
          key={spec.key}
          ref={el => { elRefsRef.current[index] = el; }}
          data-voffset="-50%"
          style={{
            ...cardBase,
            background: 'rgba(6,182,212,0.12)',
            border: '1px solid rgba(6,182,212,0.55)',
            borderRadius: 5,
            padding: '4px 6px',
          }}
        >
          {isOutlet ? (
            <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
              <rect x="1" y="1" width="10" height="10" rx="2" stroke="#22d3ee" strokeWidth="1"/>
              <line x1="4" y1="3.5" x2="4" y2="6" stroke="#22d3ee" strokeWidth="1.2" strokeLinecap="round"/>
              <line x1="8" y1="3.5" x2="8" y2="6" stroke="#22d3ee" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
          ) : (
            <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
              <rect x="1" y="1" width="10" height="10" rx="2" stroke="#22d3ee" strokeWidth="1"/>
              <rect x="3.5" y="2.5" width="2" height="4" rx="1" fill="#22d3ee"/>
              <rect x="6.5" y="5.5" width="2" height="4" rx="1" fill="#22d3ee" opacity="0.4"/>
            </svg>
          )}
        </div>
      );
    }

    return null;
  }

  return (
    <>
      {labelSpecs.map((spec, i) => renderLabel(spec, i))}
    </>
  );
}

export default WallTakeoffOverlay;
